import React, { useState } from 'react'
import { useAtom } from 'jotai'
import {
  ArrowUp,
  Check,
  ChevronDown,
  MoreHorizontal,
  SquarePen,
  Trash,
  X,
} from 'lucide-react'
import { CommentViewer } from './comment-editor/comment-viewer'
import { currentUserAtom } from '@/stores/sessionStore'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'
// import { Textarea } from '@/components/ui/textarea'
import { CommentEditor } from './comment-editor/comment-editor'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { UserAvatar } from '@/components/avatar'
import { ConfirmDialog } from '@/components/confirm-dialog'
import type { Comment } from './types'

interface CommentThreadProps {
  parent: Comment
  replies: Comment[]
  onReply: (parentId: string, content: string) => void
  onResolve: (
    commentId: string,
    resolution: 'RESOLVED' | 'DISMISSED' | 'IMPLEMENTED'
  ) => void
  onEditComment?: (commentId: string, content: string) => void
  onDeleteComment?: (commentId: string) => void
  onApplySuggestion?: (commentId: string) => void
  onDismissCheck?: (commentId: string) => void
  showSeparator?: boolean
  // 仅展示内容（隐藏回复输入与操作按钮）。用于 block 右侧按钮弹窗
  showContent?: boolean
  // 新增：当点击父评论（线程）内容区域时回调，用于驱动右侧面板跳转
  onActivate?: () => void
  members?: Array<{ id: string; name?: string | null; email?: string | null }>
}

export function CommentThread({
  parent,
  replies,
  onReply,
  onResolve,
  onEditComment,
  onDeleteComment,
  onApplySuggestion,
  onDismissCheck,
  showSeparator = false,
  showContent = false,
  onActivate,
  members = [],
}: CommentThreadProps) {
  const [replyContent, setReplyContent] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')
  const [isExpanded, setIsExpanded] = useState(true)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const [currentUser] = useAtom(currentUserAtom)

  const isCheckComment = (comment: Comment) => {
    // comment.source === 'AUTOMATIC_CHECK'
    return comment.source === 'AUTOMATIC_CHECK'
  }

  const getCheckTypeLabel = (comment: Comment) => {
    const typeLabels: Record<string, string> = {
      grammar: 'Grammar',
      brevity: 'Brevity',
      readability: 'Readability',
      repetition: 'Repetition',
      passiveVoice: 'Passive Voice',
      confidence: 'Confidence',
      citation: 'Citation',
      cliches: 'Clichés',
      aiGapDetection: 'Gap Detection',
      brokenLink: 'Broken Link',
      custom: 'Custom Check',
    }
    return typeLabels[comment.metadata?.checkType] || 'Check'
  }

  const formatTime = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date
    const now = new Date()
    const diff = now.getTime() - d.getTime()
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(hours / 24)

    if (days > 0) return `${days}d`
    if (hours > 0) return `${hours}h`
    return 'now'
  }

  const handleEdit = (commentId: string) => {
    const comment = [parent, ...replies].find((c) => c.id === commentId)
    if (comment) {
      setEditingId(commentId)
      setEditContent(comment.text || comment.content || '')
    }
  }

  const handleSaveEdit = (commentId: string) => {
    if (editContent.trim() && onEditComment) {
      onEditComment(commentId, editContent.trim())
      setEditingId(null)
      setEditContent('')
    }
  }

  const handleDelete = (commentId: string) => {
    if (onDeleteComment) {
      setDeleteDialogOpen(true)
      setPendingDeleteId(commentId)
    }
  }

  const handleReply = () => {
    if (replyContent.trim()) {
      onReply(parent.id, replyContent.trim())
      setReplyContent('')
    }
  }

  return (
    <>
      <div className='comment-thread'>
        {/* Add separator between threads */}
        {showSeparator && (
          <div className='bg-border border-foreground/10 m-0 h-px w-full' />
        )}
        <div className=''>
          {/* Parent comment */}
          <div
            className={`comment !p-0 ${isCheckComment(parent) ? 'comment-automatic-check' : ''}`}
            // onClick={onActivate}
          >
            <div className='comment-header bg-accent group/header !mb-0 h-12 p-3'>
              <div className='comment-author w-full'>
                <div
                  className={`comment-avatar ${isCheckComment(parent) ? 'comment-check-avatar' : ''}`}
                >
                  {isCheckComment(parent) ? (
                    <svg
                      className='h-3 w-3'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                    >
                      <path
                        fillRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : parent.user?.image ? (
                    <img loading="lazy" src={parent.user.image} alt={parent.user.name || ''} />
                  ) : (
                    <div className='comment-avatar-placeholder'>
                      {parent.user?.name?.[0] || '?'}
                    </div>
                  )}
                </div>
                <span className='comment-author-name'>
                  {isCheckComment(parent)
                    ? getCheckTypeLabel(parent)
                    : parent.user?.name || 'Unknown'}
                </span>
                {isCheckComment(parent) && parent.metadata?.severity && (
                  <span
                    className={`comment-severity comment-severity-${parent.metadata.severity}`}
                  >
                    {parent.metadata.severity}
                  </span>
                )}
                <span className='comment-time'>
                  {formatTime(parent.createdAt)}
                </span>
              </div>
              {isCheckComment(parent) ? (
                <div className='comment-check-actions'>
                  {/* actions for automatic check (kept as-is) */}
                  <Button
                    variant='ghost'
                    className='size-5 w-5 !rounded-[5px] !border-none !p-0.5'
                    onClick={() => setIsExpanded(!isExpanded)}
                  >
                    <ChevronDown
                      size={16}
                      className={cn(
                        '!transition-all',
                        isExpanded && 'rotate-180'
                      )}
                    />
                  </Button>

                  {/* optional apply/dismiss buttons are below; three-dot menu is hidden when showContent */}
                </div>
              ) : (
                parent.commentStatus === 'OPEN' && (
                  <div className='comment-header-actions'>
                    {!showContent && (
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger
                          asChild
                          className='opacity-0 group-hover/header:opacity-100'
                        >
                          <Button
                            variant='ghost'
                            className='size-5 w-5 rounded-[5px] !p-0.5'
                            tooltip='More actions'
                          >
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className='z-[10000] w-full'>
                          {/* <DropdownMenuItem onClick={() => onResolve(parent.id)}>
                          <Check className='size-4' /> Resolve comment
                        </DropdownMenuItem> */}
                          {parent.source === 'USER' && onEditComment && (
                            <DropdownMenuItem
                              onClick={() => handleEdit(parent.id)}
                            >
                              <SquarePen className='size-4' /> Edit
                            </DropdownMenuItem>
                          )}
                          {parent.source === 'USER' && onDeleteComment && (
                            <DropdownMenuItem
                              variant='destructive'
                              onClick={() => handleDelete(parent.id)}
                            >
                              <Trash className='size-4' /> Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    <Button
                      variant='ghost'
                      className='size-5 w-5 !rounded-[5px] !border-none !p-0.5'
                      onClick={() => setIsExpanded(!isExpanded)}
                    >
                      <ChevronDown
                        size={16}
                        className={cn(
                          'transition-transform duration-200',
                          isExpanded && 'rotate-180'
                        )}
                      />
                    </Button>

                    {/* edit/delete inline buttons hidden; use dropdown when allowed */}
                  </div>
                )
              )}
            </div>
          </div>

          {isExpanded && (
            <div className='bg-background w-full overflow-hidden p-3 pb-0'>
              {editingId === parent.id ? (
                <div className='comment-edit-box'>
                  <CommentEditor
                    members={members}
                    value={editContent}
                    autoFocus
                    maxHeightClass='max-h-40'
                    onSubmit={({ text }) => {
                      setEditContent(text)
                      handleSaveEdit(parent.id)
                    }}
                    onCancel={() => {
                      setEditingId(null)
                      setEditContent('')
                    }}
                  />
                </div>
              ) : (
                <div
                  className='comment-content pb-3 pl-[28px]'
                  onClick={onActivate}
                >
                  {/* Show quoted text for range comments within the thread */}
                  {parent.scope === 'RANGE' && parent.textRange?.text && (
                    <div
                      className={`text-muted-foreground mb-1.5 w-full pl-3 text-[13px] leading-[18px]`}
                      style={{
                        borderLeft: `2px solid ${isCheckComment(parent) ? 'rgba(147, 51, 234, 0.5)' : 'rgba(251, 191, 36, 0.5)'}`,
                      }}
                    >
                      <Tooltip>
                        <TooltipTrigger className='w-fit max-w-full truncate text-left'>
                          "{parent.textRange.text}"
                        </TooltipTrigger>
                        <TooltipContent className='max-w-[400px]'>
                          {parent.textRange.text}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  )}

                  <CommentViewer
                    json={(parent as any)?.metadata?.content}
                    text={parent.text || parent.content || ''}
                  />
                </div>
              )}
              {/* Replies */}
              {replies.map((reply) => (
                <div
                  key={reply.id}
                  className='comment comment-reply'
                  onClick={onActivate}
                >
                  <div className='comment-header group/reply-header'>
                    <div className='comment-author'>
                      <div className='comment-avatar'>
                        {reply.user?.image ? (
                          <img
                            src={reply.user.image}
                            alt={reply.user.name || ''}
                          />
                        ) : (
                          <div className='comment-avatar-placeholder'>
                            {reply.user?.name?.[0] || '?'}
                          </div>
                        )}
                      </div>
                      <span className='comment-author-name'>
                        {reply.user?.name || 'Unknown'}
                      </span>
                      <span className='comment-time'>
                        {formatTime(reply.createdAt)}
                      </span>
                    </div>
                    {reply.commentStatus === 'OPEN' && (
                      <div className='comment-header-actions opacity-0 group-hover/reply-header:opacity-100'>
                        {!showContent &&
                          reply.source === 'USER' &&
                          (onEditComment || onDeleteComment) && (
                            <DropdownMenu modal={false}>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant='ghost'
                                  className='size-5 w-5 rounded-[5px] !p-0.5'
                                >
                                  <MoreHorizontal />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className='z-[10000] w-full'>
                                <DropdownMenuItem
                                  onClick={() => handleEdit(reply.id)}
                                  disabled={!onEditComment}
                                  className='flex items-center gap-2'
                                >
                                  <SquarePen className='size-4' /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDelete(reply.id)}
                                  disabled={!onDeleteComment}
                                  variant='destructive'
                                >
                                  <Trash className='size-4' /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}

                        {/* inline edit/delete buttons hidden when showContent is true */}
                      </div>
                    )}
                  </div>
                  {editingId === reply.id ? (
                    <div className='comment-edit-box'>
                      <CommentEditor
                        members={members}
                        value={editContent}
                        autoFocus
                        maxHeightClass='max-h-32'
                        onSubmit={({ text }) => {
                          setEditContent(text)
                          handleSaveEdit(reply.id)
                        }}
                        onCancel={() => {
                          setEditingId(null)
                          setEditContent('')
                        }}
                      />
                    </div>
                  ) : (
                    <div className='comment-content pl-7'>
                      <CommentViewer
                        json={(reply as any)?.metadata?.content}
                        text={reply.text || reply.content || ''}
                      />
                    </div>
                  )}
                </div>
              ))}
              {/* Reply input at the end of each thread (only for open comments) */}
              {!showContent &&
                parent.commentStatus === 'OPEN' &&
                parent.source !== 'AUTOMATIC_CHECK' && (
                  <div className='comment-reply comment-new pl-0!'>
                    <div className='comment-new-wrapper mt-2 mb-2.5 !h-[31px]'>
                      {/* <div className='comment-avatar comment-new-avatar'>
              <div className='comment-avatar-placeholder'>T</div>
            </div> */}
                      <UserAvatar
                        name={currentUser?.name ?? ''}
                        image={currentUser?.image ?? ''}
                        className='mt-1 size-5 rounded-full'
                      />
                      <div className='relative flex-1'>
                        <CommentEditor
                          members={members}
                          autoFocus={false}
                          placeholder='Reply…'
                          maxHeightClass='max-h-28'
                          onSubmit={({ text /*, json, mentions*/ }) => {
                            setReplyContent('')
                            onReply(parent.id, text)
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              {/* action：当 showContent 为 true 时隐藏操作按钮 */}
              {!showContent && (
                <>
                  <Separator className='bg-foreground/6 my-0 -ml-3 !w-[calc(100%+24px)]' />
                  <div className='flex gap-2 py-2.5'>
                    {isCheckComment(parent) ? (
                      <>
                        {parent.metadata?.suggestion && onApplySuggestion && (
                          <Button
                            className='!h-7 gap-1.5 !px-2.5 !py-2'
                            onClick={(e) => {
                              e.stopPropagation()
                              onApplySuggestion(parent.id)
                            }}
                          >
                            <Check />
                            Apply
                          </Button>
                        )}
                        {onDismissCheck && (
                          <Button
                            variant='outline'
                            className='!h-7 gap-1.5 !px-2.5 !py-2'
                            onClick={() => onDismissCheck(parent.id)}
                          >
                            <X />
                            Dismiss
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <Button
                          className='!h-7 gap-1.5 !px-2.5 !py-2'
                          onClick={() => onResolve(parent.id, 'RESOLVED')}
                        >
                          <Check />
                          Resolve
                        </Button>
                        <Button
                          variant='outline'
                          className='!h-7 gap-1.5 !px-2.5 !py-2'
                          onClick={() => onResolve(parent.id, 'DISMISSED')}
                        >
                          <X />
                          Discard
                        </Button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open)
          if (!open) setPendingDeleteId(null)
        }}
        title='Delete comment'
        desc='Are you sure you want to delete this comment?'
        destructive
        confirmText='Delete'
        handleConfirm={() => {
          if (pendingDeleteId) onDeleteComment?.(pendingDeleteId)
          setDeleteDialogOpen(false)
          setPendingDeleteId(null)
        }}
      />
    </>
  )
}
